import React from 'react';
import Form from 'react-bootstrap/Form';
import Select from 'react-select';
import $ from 'jquery';
import { normalizeChildDatasetName } from '../../utils/attribute_name_utils';

// Module-level cache: survives component remounts (filter open/close cycles).
// Only grows — filtered inputData from applyFilter calls never shrinks it.
let cachedBusinessAreas = [];
// Subject area cache keyed by business_area_id — accumulates across all fetches.
let cachedSubjectAreas = {}; // { [businessAreaId]: [{ label, value }] }
// KPI cache keyed by subject_area_id — accumulates across all fetches.
let cachedKpis = {}; // { [subjectAreaId]: [{ label, value }] }

class DiscoverForm extends React.Component {
  getSubjectAreaIdFromKey = (key) => {
    const keyParts = String(key).split("_");
    return Number(keyParts[keyParts.length - 1]);
  };

  normalizeWatchlistData = (watchlistData = {}) => {
    if (Array.isArray(watchlistData.kpis)) {
      return watchlistData;
    }

    const businessAreas = Array.isArray(watchlistData.business_areas)
      ? watchlistData.business_areas.map(ba => ({
        label: ba.label || ba.name,
        value: ba.value || ba.id || ba.business_id
      }))
      : [];
    const subjectAreas = [];
    const kpiMap = new Map();
    const integrationMap = new Map();
    const datasetMap = new Map();
    const attributeMap = new Map();

    Object.entries(watchlistData.subject_areas || {}).forEach(([businessAreaId, subjectAreaList]) => {
      (subjectAreaList || []).forEach(subjectArea => {
        subjectAreas.push({
          label: subjectArea.label || subjectArea.name,
          value: subjectArea.value || subjectArea.id,
          business_area_id: Number(businessAreaId)
        });
      });
    });

    Object.entries(watchlistData.kpis || {}).forEach(([subjectKey, kpiList]) => {
      const subjectAreaId = this.getSubjectAreaIdFromKey(subjectKey);

      (kpiList || []).forEach(kpi => {
        const kpiId = kpi.value || kpi.id;

        if (!kpiMap.has(kpiId)) {
          kpiMap.set(kpiId, {
            label: kpi.label || kpi.name,
            value: kpiId,
            subject_area_id: kpi.subject_area_id || subjectAreaId,
            integration_ids: new Set(),
            dataset_ids: new Set(),
            attribute_ids: new Set()
          });
        }

        const kpiEntry = kpiMap.get(kpiId);

        (kpi.integrations || []).forEach(integration => {
          const integrationId = integration.value || integration.id || integration.datasource_id;
          if (!integrationId) return;

          if (!integrationMap.has(integrationId)) {
            integrationMap.set(integrationId, {
              label: integration.label || integration.name || `Integration ${integrationId}`,
              value: integrationId,
              env_id: integration.env_id || this.props.selectedEnvId,
              datasource_id: integrationId,
              data_set_ids: new Set()
            });
          }

          kpiEntry.integration_ids.add(integrationId);
        });

        Object.entries(kpi.datasets || {}).forEach(([integrationIdKey, datasetList]) => {
          const integrationId = Number(integrationIdKey);
          if (!integrationMap.has(integrationId)) {
            integrationMap.set(integrationId, {
              label: `Integration ${integrationId}`,
              value: integrationId,
              env_id: this.props.selectedEnvId,
              datasource_id: integrationId,
              data_set_ids: new Set()
            });
          }

          kpiEntry.integration_ids.add(integrationId);

          (datasetList || []).forEach(dataset => {
            const datasetId = dataset.value || dataset.id;
            if (!datasetId) return;

            if (!datasetMap.has(datasetId)) {
              datasetMap.set(datasetId, {
                label: dataset.label || dataset.name,
                value: datasetId,
                id: datasetId,
                integration_ids: integrationId,
                attribute_ids: new Set()
              });
            }

            kpiEntry.dataset_ids.add(datasetId);
            integrationMap.get(integrationId).data_set_ids.add(datasetId);
          });
        });

        Object.entries(kpi.attributes || {}).forEach(([datasetIdKey, attributeList]) => {
          const datasetId = Number(datasetIdKey);

          (attributeList || []).forEach(attribute => {
            const attributeId = attribute.value || attribute.id;
            if (!attributeId) return;

            if (!attributeMap.has(attributeId)) {
              attributeMap.set(attributeId, {
                label: attribute.label || attribute.name,
                value: attributeId,
                data_set_id: datasetId
              });
            }

            if (datasetMap.has(datasetId)) {
              datasetMap.get(datasetId).attribute_ids.add(attributeId);
            }
            kpiEntry.attribute_ids.add(attributeId);
          });
        });
      });
    });

    return {
      business_areas: businessAreas,
      subject_areas: subjectAreas,
      kpis: Array.from(kpiMap.values()).map(kpi => ({
        ...kpi,
        integration_ids: Array.from(kpi.integration_ids),
        dataset_ids: Array.from(kpi.dataset_ids),
        attribute_ids: Array.from(kpi.attribute_ids)
      })),
      integrations: Array.from(integrationMap.values()).map(integration => ({
        ...integration,
        data_set_ids: Array.from(integration.data_set_ids)
      })),
      datasets: Array.from(datasetMap.values()).map(dataset => ({
        ...dataset,
        attribute_ids: Array.from(dataset.attribute_ids)
      })),
      attributes: Array.from(attributeMap.values())
    };
  };

  getInputData = () => {
    const inputData = this.props.inputData || {};
    const watchlistData = this.props.selectedWatchlistId
      ? (this.props.selectedWatchlistData || inputData.watch_list_data)
      : null;

    return watchlistData
      ? this.normalizeWatchlistData(watchlistData)
      : inputData;
  };

  getDefaultFilterState = () => {
    const inputData = this.getInputData();
    const subjectAreas = inputData.subject_areas || [];

    // Always use the preserved full list, never the (possibly filtered) inputData
    const businessAreaOptions = this.allBusinessAreas
      ? [...this.allBusinessAreas]
      : (inputData.business_areas || []).map(ba => ({
          label: ba.label || ba.name,
          value: ba.value || ba.id
        }));

    const selectedBusinessArea = businessAreaOptions[0] || null;
    const subjectAreaOptions = selectedBusinessArea
      ? subjectAreas
        .filter(sa => Number(sa.business_area_id) === Number(selectedBusinessArea.value))
        .map(sa => ({ label: sa.label, value: sa.value }))
      : [];
    const selectedSubjectArea = subjectAreaOptions[0] || null;
    const kpiOptions = selectedSubjectArea
      ? (inputData.kpis || [])
        .filter(k => Number(k.subject_area_id) === Number(selectedSubjectArea.value))
        .map(k => ({ label: k.label, value: k.value }))
      : [];

    return {
      businessAreaOptions,
      subjectAreaOptions,
      kpiOptions,
      datasourceOptions: [],
      datasetOptions: [],
      attributeOptions: [],
      selectedBusinessArea,
      selectedSubjectArea,
      selectedKpi: null,
      selectedDatasource: null,
      selectedDataset: null,
      selectedAttribute: null
    };
  };

  constructor(props) {
    super(props);
    this.changeBusinessArea = this.changeBusinessArea.bind(this);
    this.changeSubjectArea = this.changeSubjectArea.bind(this);
    this.changeKpi = this.changeKpi.bind(this);
    this.changeDataSource = this.changeDataSource.bind(this);
    this.changeDataset = this.changeDataset.bind(this);
    this.changeAttribute = this.changeAttribute.bind(this);

    const inputData = this.getInputData();
    const businessAreas = inputData.business_areas || [];
    const subjectAreas = inputData.subject_areas || [];

    // Update the module-level cache only when we receive more BAs than we have cached.
    // This ensures filtered inputData (after applyFilter) never wipes out the full list.
    if (businessAreas.length > cachedBusinessAreas.length) {
      cachedBusinessAreas = businessAreas.map(ba => ({
        label: ba.label || ba.name,
        value: ba.value || ba.id
      }));
    }
    this.allBusinessAreas = cachedBusinessAreas;

    // Merge all subject areas into the cache so they survive filtered inputData updates
    subjectAreas.forEach(sa => {
      const baId = sa.business_area_id;
      if (!cachedSubjectAreas[baId]) cachedSubjectAreas[baId] = [];
      if (!cachedSubjectAreas[baId].find(c => c.value === sa.value)) {
        cachedSubjectAreas[baId].push({ label: sa.label, value: sa.value });
      }
    });

    // Merge all KPIs into the cache keyed by subject_area_id
    const kpis = inputData.kpis || [];
    kpis.forEach(k => {
      const saId = k.subject_area_id;
      if (!cachedKpis[saId]) cachedKpis[saId] = [];
      if (!cachedKpis[saId].find(c => c.value === k.value)) {
        cachedKpis[saId].push({ label: k.label, value: k.value });
      }
    });

    let businessAreaOptions = [...this.allBusinessAreas];

    let selectedBusinessArea = businessAreaOptions[0] || null;
    let subjectAreaOptions = [];

    // Pre-populate Subject Areas only for that first Business Area
    if (selectedBusinessArea) {
      subjectAreaOptions = subjectAreas
        .filter(sa => Number(sa.business_area_id) === Number(selectedBusinessArea.value))
        .map(sa => ({ label: sa.label, value: sa.value }));
    }

    let selectedSubjectArea = subjectAreaOptions[0] || null;
    let kpiOptions = [];

    // Pre-populate KPIs for the first Subject Area
    if (selectedSubjectArea) {
      kpiOptions = (inputData.kpis || [])
        .filter(k => Number(k.subject_area_id) === Number(selectedSubjectArea.value))
        .map(k => ({ label: k.label, value: k.value }));
    }

    // Handle pre-selected values from props
    let selectedKpi = null;
    let selectedDatasource = null;
    let selectedDataset = null;
    let selectedAttribute = null;
    let datasourceOptions = [];
    let datasetOptions = [];
    let attributeOptions = [];

    // Set pre-selected KPI if provided
    if (this.props.preSelectedKpi && this.props.preSelectedKpi.value) {
      selectedKpi = this.props.preSelectedKpi;
      
      // If KPI is pre-selected, we need to find and set the corresponding subject area and business area
      const inputData = this.getInputData();
      const kpis = inputData.kpis || [];
      const selectedKpiItem = kpis.find(k => Number(k.value) === Number(selectedKpi.value));
      
      if (selectedKpiItem) {
        // Find the subject area for this KPI
        const subjectAreas = inputData.subject_areas || [];
        const kpiSubjectArea = subjectAreas.find(sa => Number(sa.value) === Number(selectedKpiItem.subject_area_id));
        
        if (kpiSubjectArea) {
          // Find the business area for this subject area
          const businessAreas = inputData.business_areas || [];
          const kpiBusinessArea = businessAreas.find(ba => Number(ba.value) === Number(kpiSubjectArea.business_area_id));
          
          if (kpiBusinessArea) {
            selectedBusinessArea = { label: kpiBusinessArea.label, value: kpiBusinessArea.value };
            // Always use the preserved full list
            businessAreaOptions = this.allBusinessAreas ? [...this.allBusinessAreas] : businessAreas.map(ba => ({
              label: ba.label || ba.name,
              value: ba.value || ba.id
            }));
            
            // Set subject area and its options
            selectedSubjectArea = { label: kpiSubjectArea.label, value: kpiSubjectArea.value };
            subjectAreaOptions = subjectAreas
              .filter(sa => Number(sa.business_area_id) === Number(selectedBusinessArea.value))
              .map(sa => ({ label: sa.label, value: sa.value }));
            
            // Set KPI options for this subject area
            kpiOptions = kpis
              .filter(k => Number(k.subject_area_id) === Number(selectedSubjectArea.value))
              .map(k => ({ label: k.label, value: k.value }));
            
            // Pre-populate datasource options for this KPI
            const integrations = inputData.integrations || [];
            const allowedIds = selectedKpiItem.integration_ids?.map(Number) || [];
            datasourceOptions = integrations
              .filter(int => allowedIds.includes(Number(int.value)))
              .map(int => ({ label: int.label, value: int.value }));
          }
        }
      }
    }

    this.state = {
      businessAreaOptions,
      subjectAreaOptions,
      kpiOptions,
      datasourceOptions,
      datasetOptions,
      attributeOptions,
      selectedBusinessArea,
      selectedSubjectArea,
      selectedKpi,
      selectedDatasource,
      selectedDataset,
      selectedAttribute
    };
  }

  /**
   * RECURSIVE TREE TRIGGER
   * Ensures parents remain open while opening children to avoid double-clicks.
   * Scrolls the final target node into view inside the left panel's scrollable container.
   */
  triggerTreeClick(type, label, parentChain = []) {
    const clickNodeIfClosed = (nodeType, nodeLabel) => {
      let target = $(`div[id^='${nodeType}-left-panel-div-']`).filter(function() {
        return $(this).text().trim() === nodeLabel;
      });

      if (target.length) {
        const isAlreadyOpen = target.hasClass('caret-down') || target.closest('li').hasClass('active');
        if (!isAlreadyOpen) {
          target.trigger('click');
        }
        return target;
      }
      return null;
    };

    // // Close every open BusinessArea node except the one being selected
    // const closeOtherBusinessAreas = (selectedLabel) => {
    //   $(`div[id^='BusinessArea-left-panel-div-']`).each(function() {
    //     const node = $(this);
    //     if (node.text().trim() === selectedLabel) return; // skip the selected one
    //     const isOpen = node.hasClass('caret-down') || node.closest('li').hasClass('active');
    //     if (isOpen) {
    //       node.trigger('click'); // collapse it
    //     }
    //   });
    // };

    // Walk up the DOM to find the nearest scrollable ancestor
    const getScrollParent = (node) => {
      if (node == null || node === document.body) return null;
      if (node.scrollHeight > node.clientHeight) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
      }
      return getScrollParent(node.parentNode);
    };

    // Scroll the element to the center of its scrollable parent
    const scrollToCenter = (element) => {
      if (!element) return;
      const scrollParent = getScrollParent(element);
      if (scrollParent) {
        const parentRect = scrollParent.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const offset = elementRect.top - parentRect.top - (parentRect.height / 2) + (elementRect.height / 2);
        scrollParent.scrollBy({ top: offset, behavior: 'smooth' });
      } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };


    // // If selecting a BusinessArea, close all other open BA nodes first
    // if (type === 'BusinessArea') {
    //   closeOtherBusinessAreas(label);
    // }

    parentChain.forEach((parent, index) => {
      setTimeout(() => {
        clickNodeIfClosed(parent.type, parent.label);
      }, index * 150);
    });

    // Click the target node, then scroll it into center of the left panel
    setTimeout(() => {
      const targetEl = clickNodeIfClosed(type, label);
      if (targetEl && targetEl.length) {
        // Extra delay so the tree finishes expanding before we scroll
        setTimeout(() => {
          scrollToCenter(targetEl[0]);
        }, 150);
      }
    }, (parentChain.length * 150) + 100);
  }

  changeBusinessArea(event) {
    const envOption = { value: this.props.selectedEnvId, label: this.props.latest_env_name || '' };

    // Directly fetch data for this Business Area — don't rely solely on the tree click,
    // because if this BA node is already open, triggerTreeClick skips the click
    // and no data refresh happens (causes empty subject areas on first BA re-selection).
    this.props.applyFilter('BusinessArea', envOption, null, null, null, null, null, event);

    // Use cached subject areas first (always available regardless of filtered inputData)
    const cachedOptions = (cachedSubjectAreas[event.value] || []);

    // Also check current inputData as secondary source
    const inputData = this.getInputData();
    const subjectAreas = inputData.subject_areas || [];
    const freshOptions = subjectAreas
      .filter(sa => Number(sa.business_area_id) === Number(event.value))
      .map(sa => ({ label: sa.label, value: sa.value }));

    // Prefer whichever has more entries
    const options = cachedOptions.length >= freshOptions.length ? cachedOptions : freshOptions;

    this.setState({
      selectedBusinessArea: event,
      selectedSubjectArea: null,
      selectedKpi: null,
      selectedDatasource: null,
      selectedDataset: null,
      selectedAttribute: null,
      subjectAreaOptions: options,
      kpiOptions: [],
      datasourceOptions: [],
      datasetOptions: [],
      attributeOptions: []
    }, () => {
      this.triggerTreeClick('BusinessArea', event.label);
    });

    $('.filter-count').html(1).show();
  }

  changeSubjectArea(event) {
    const envOption = { value: this.props.selectedEnvId, label: this.props.latest_env_name || '' };
    
    // 1. Fetch latest KPI data for this Subject Area immediately
    this.props.applyFilter('SubjectArea', envOption, null, null, null, null, event, this.state.selectedBusinessArea);

    // 2. Use cached KPIs first (always available regardless of filtered inputData)
    const cachedKpiOptions = cachedKpis[event.value] || [];

    // 3. Also check current inputData as secondary source
    const currentInputData = this.getInputData();
    const existingKpis = currentInputData.kpis || [];
    const freshKpiOptions = existingKpis
      .filter(k => Number(k.subject_area_id) === Number(event.value))
      .map(k => ({ label: k.label, value: k.value }));

    // Prefer whichever has more entries
    const immediateOptions = cachedKpiOptions.length >= freshKpiOptions.length
      ? cachedKpiOptions
      : freshKpiOptions;

    this.setState({
      selectedSubjectArea: event,
      selectedKpi: null,
      kpiOptions: immediateOptions, // Use what we have immediately
      datasourceOptions: [],
      datasetOptions: [],
      attributeOptions: []
    }, () => {
      this.triggerTreeClick('SubjectArea', event.label, [
        { type: 'BusinessArea', label: this.state.selectedBusinessArea?.label }
      ]);
    });

    $('.filter-count').html(2);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedWatchlistId !== this.props.selectedWatchlistId) {
      this.setState(this.getDefaultFilterState());
      return;
    }

    if (prevProps.inputData !== this.props.inputData && this.props.selectedWatchlistId) {
      this.setState(this.getDefaultFilterState());
      return;
    }

    // If inputData changed and a business area is selected, refresh subjectAreaOptions.
    // This handles the case where the first BA click triggers an async data fetch —
    // once inputData arrives, we populate subject areas without requiring a second click.
    if (prevProps.inputData !== this.props.inputData && this.state.selectedBusinessArea) {
      const inputData = this.getInputData();
      const subjectAreas = inputData.subject_areas || [];

      // Merge newly arrived subject areas into the module-level cache
      subjectAreas.forEach(sa => {
        const baId = sa.business_area_id;
        if (!cachedSubjectAreas[baId]) cachedSubjectAreas[baId] = [];
        if (!cachedSubjectAreas[baId].find(c => c.value === sa.value)) {
          cachedSubjectAreas[baId].push({ label: sa.label, value: sa.value });
        }
      });

      // Read from cache (always has the full accumulated list)
      const newSubjectAreaOptions = cachedSubjectAreas[this.state.selectedBusinessArea.value] || [];

      if (JSON.stringify(newSubjectAreaOptions) !== JSON.stringify(this.state.subjectAreaOptions)) {
        this.setState({ subjectAreaOptions: newSubjectAreaOptions });
      }
    }

    // If inputData changed (likely due to applyFilter in changeSubjectArea), update kpiOptions
    if (prevProps.inputData !== this.props.inputData && this.state.selectedSubjectArea) {
      const kpis = this.getInputData()?.kpis || [];

      // Merge newly arrived KPIs into the module-level cache
      kpis.forEach(k => {
        const saId = k.subject_area_id;
        if (!cachedKpis[saId]) cachedKpis[saId] = [];
        if (!cachedKpis[saId].find(c => c.value === k.value)) {
          cachedKpis[saId].push({ label: k.label, value: k.value });
        }
      });

      // Read from cache (always has the full accumulated list)
      const options = cachedKpis[this.state.selectedSubjectArea.value] || [];
      
      if (JSON.stringify(options) !== JSON.stringify(this.state.kpiOptions)) {
        this.setState({ kpiOptions: options });
      }
    }

    // Handle preSelectedKpi prop changes (compare by value, not reference, to avoid spurious resets)
    const prevKpiValue = prevProps.preSelectedKpi?.value;
    const currKpiValue = this.props.preSelectedKpi?.value;
    if (prevKpiValue !== currKpiValue && currKpiValue) {
      const inputData = this.getInputData();
      const kpis = inputData.kpis || [];
      const selectedKpiItem = kpis.find(k => Number(k.value) === Number(this.props.preSelectedKpi.value));
      
      if (selectedKpiItem) {
        // Find the subject area for this KPI
        const subjectAreas = inputData.subject_areas || [];
        const kpiSubjectArea = subjectAreas.find(sa => Number(sa.value) === Number(selectedKpiItem.subject_area_id));
        
        if (kpiSubjectArea) {
          // Find the business area for this subject area
          const businessAreas = inputData.business_areas || [];
          const kpiBusinessArea = businessAreas.find(ba => Number(ba.value) === Number(kpiSubjectArea.business_area_id));
          
          if (kpiBusinessArea) {
            const selectedBusinessArea = { label: kpiBusinessArea.label, value: kpiBusinessArea.value };
            // Always use the preserved full list
            const businessAreaOptions = this.allBusinessAreas ? [...this.allBusinessAreas] : businessAreas.map(ba => ({
              label: ba.label || ba.name,
              value: ba.value || ba.id
            }));
            
            // Set subject area and its options
            const selectedSubjectArea = { label: kpiSubjectArea.label, value: kpiSubjectArea.value };
            const subjectAreaOptions = subjectAreas
              .filter(sa => Number(sa.business_area_id) === Number(selectedBusinessArea.value))
              .map(sa => ({ label: sa.label, value: sa.value }));
            
            // Set KPI options for this subject area
            const kpiOptions = kpis
              .filter(k => Number(k.subject_area_id) === Number(selectedSubjectArea.value))
              .map(k => ({ label: k.label, value: k.value }));
            
            // Pre-populate datasource options for this KPI
            const integrations = inputData.integrations || [];
            const allowedIds = selectedKpiItem.integration_ids?.map(Number) || [];
            const datasourceOptions = integrations
              .filter(int => allowedIds.includes(Number(int.value)))
              .map(int => ({ label: int.label, value: int.value }));
            
            this.setState({
              selectedBusinessArea,
              selectedSubjectArea,
              selectedKpi: this.props.preSelectedKpi,
              businessAreaOptions,
              subjectAreaOptions,
              kpiOptions,
              datasourceOptions,
              selectedDatasource: null,
              datasetOptions: [],
              attributeOptions: []
            });
          }
        }
      }
    }
  }

  changeKpi(event) {
    const inputData = this.getInputData();
    const kpis = inputData.kpis || [];
    const integrations = inputData.integrations || [];

    const selectedKpiItem = kpis.find((item) => Number(item.value) === Number(event.value));
    const allowedIds = selectedKpiItem?.integration_ids?.map(Number) || [];

    // Filter Integrations allowed for this KPI
    const options = integrations
      .filter(int => allowedIds.includes(Number(int.value)))
      .map(int => ({ label: int.label, value: int.value }));

    this.setState({
      selectedKpi: event,
      selectedDatasource: null,
      datasourceOptions: options,
      datasetOptions: [],
      attributeOptions: []
    });

    this.triggerTreeClick('KPI', event.label, [
      { type: 'BusinessArea', label: this.state.selectedBusinessArea?.label },
      { type: 'SubjectArea', label: this.state.selectedSubjectArea?.label }
    ]);

    const envOption = { value: this.props.selectedEnvId, label: this.props.latest_env_name || '' };
    this.props.applyFilter('KPI', envOption, null, null, null, event, this.state.selectedSubjectArea, this.state.selectedBusinessArea);
    $('.filter-count').html(3);
  }

  changeDataSource(event) {
    const inputData = this.getInputData();
    const datasets = inputData.datasets || [];
    const kpiItem = inputData.kpis?.find(k => Number(k.value) === Number(this.state.selectedKpi?.value));
    const intItem = inputData.integrations?.find(i => Number(i.value) === Number(event.value));

    const kpiDsIds = kpiItem?.dataset_ids?.map(Number) || [];
    const intDsIds = intItem?.data_set_ids?.map(Number) || [];

    // Filter datasets that belong to both the KPI and the chosen Integration
    const options = datasets
      .filter(ds => kpiDsIds.includes(Number(ds.value)) && intDsIds.includes(Number(ds.value)))
      .map(ds => ({ label: normalizeChildDatasetName(ds.label), value: ds.value }));

    this.setState({ selectedDatasource: event, selectedDataset: null, datasetOptions: options, attributeOptions: [] });

    this.triggerTreeClick('Datasource', event.label, [
      { type: 'BusinessArea', label: this.state.selectedBusinessArea?.label },
      { type: 'SubjectArea', label: this.state.selectedSubjectArea?.label },
      { type: 'KPI', label: this.state.selectedKpi?.label }
    ]);

    const envOption = { value: this.props.selectedEnvId, label: this.props.latest_env_name || '' };
    this.props.applyFilter('Datasource', envOption, event, null, null, this.state.selectedKpi, this.state.selectedSubjectArea, this.state.selectedBusinessArea);
    $('.filter-count').html(4);
  }

  changeDataset(event) {
    const inputData = this.getInputData();
    const attributes = inputData.attributes || [];
    const kpiItem = inputData.kpis?.find(k => Number(k.value) === Number(this.state.selectedKpi?.value));
    const kpiAttrIds = kpiItem?.attribute_ids?.map(Number) || [];

    // Filter attributes by chosen dataset and KPI allowance
    const options = attributes
      .filter(attr => Number(attr.data_set_id) === Number(event.value) && kpiAttrIds.includes(Number(attr.value)))
      .map(attr => ({ label: attr.label, value: attr.value }));

    this.setState({ selectedDataset: event, selectedAttribute: null, attributeOptions: options });
    
    this.triggerTreeClick('Dataset', event.label, [
      { type: 'Datasource', label: this.state.selectedDatasource?.label }
    ]);

    const envOption = { value: this.props.selectedEnvId, label: this.props.latest_env_name || '' };
    this.props.applyFilter('Dataset', envOption, this.state.selectedDatasource, event, null, this.state.selectedKpi, this.state.selectedSubjectArea, this.state.selectedBusinessArea);
    $('.filter-count').html(5);
  }

  changeAttribute(event) {
    this.setState({ selectedAttribute: event });
    
    // Final Trigger: Clicks the tree node which renders the chart in verticalization.js
    this.triggerTreeClick('Attribute', event.label, [
      { type: 'Dataset', label: this.state.selectedDataset?.label }
    ]);

    const envOption = { value: this.props.selectedEnvId, label: this.props.latest_env_name || '' };
    this.props.applyFilter('Attribute', envOption, this.state.selectedDatasource, this.state.selectedDataset, event, this.state.selectedKpi, this.state.selectedSubjectArea, this.state.selectedBusinessArea);
    $('.filter-count').html(6);

    // Automatically close the advanced filter and the modal after a small delay
    setTimeout(() => {
      if (this.props.closeAdvancedFilter) {
        this.props.closeAdvancedFilter();
      }
      if (this.props.handleCloseAutoCompleteModal) {
        this.props.handleCloseAutoCompleteModal();
      }
    }, 400);
  }

  render() {
    return (
      <div className="qd_search-filter-form">
        <Form className="form-label-right-md">
          <h4 className="qd_autosuggest-title">Advanced search</h4>
          <button onClick={this.props.closeAdvancedFilter} type="button" className="btn btn-link">Close</button>
          <div className="row row-sm">
            <Form.Group className="col-6">
              <Form.Label>Business Area :</Form.Label>
              <Select
                options={this.state.businessAreaOptions}
                onChange={this.changeBusinessArea}
                value={this.state.selectedBusinessArea}
                classNamePrefix='select-control'
              />
            </Form.Group>
            <Form.Group className="col-6">
              <Form.Label>Subject Area :</Form.Label>
              <Select
                options={this.state.subjectAreaOptions}
                onChange={this.changeSubjectArea}
                value={this.state.selectedSubjectArea}
                isDisabled={!this.state.selectedBusinessArea}
                classNamePrefix='select-control'
              />
            </Form.Group>
            <Form.Group className="col-6">
              <Form.Label>KPI :</Form.Label>
              <Select
                options={this.state.kpiOptions}
                onChange={this.changeKpi}
                value={this.state.selectedKpi}
                isDisabled={!this.state.selectedSubjectArea}
                classNamePrefix='select-control'
              />
            </Form.Group>
            <Form.Group className="col-6">
              <Form.Label>Integration :</Form.Label>
              <Select
                options={this.state.datasourceOptions}
                onChange={this.changeDataSource}
                value={this.state.selectedDatasource}
                isDisabled={!this.state.selectedKpi}
                classNamePrefix='select-control'
              />
            </Form.Group>
            <Form.Group className="col-6">
              <Form.Label>Dataset :</Form.Label>
              <Select
                options={this.state.datasetOptions}
                onChange={this.changeDataset}
                value={this.state.selectedDataset}
                isDisabled={!this.state.selectedDatasource}
                classNamePrefix='select-control'
              />
            </Form.Group>
            <Form.Group className="col-6">
              <Form.Label>Attributes :</Form.Label>
              <Select
                options={this.state.attributeOptions}
                onChange={this.changeAttribute}
                value={this.state.selectedAttribute}
                isDisabled={!this.state.selectedDataset}
                classNamePrefix='select-control'
              />
            </Form.Group>
          </div>
          <div className="qd_search-rst-footer">
            <ol className="qd_tab-separated-block">
              <li className="tab-separated-value"><span><strong>{this.state.businessAreaOptions.length}</strong> Business Area</span></li>
              <li className="tab-separated-value"><span><strong>{this.state.subjectAreaOptions.length}</strong> Subject Area</span></li>
              <li className="tab-separated-value"><span><strong>{this.state.kpiOptions.length}</strong> KPI</span></li>
              <li className="tab-separated-value"><span><strong>{this.state.datasourceOptions.length}</strong> Datasource</span></li>
              <li className="tab-separated-value"><span><strong>{this.state.datasetOptions.length}</strong> Dataset</span></li>
              <li className="tab-separated-value"><span><strong>{this.state.attributeOptions.length}</strong> Attributes</span></li>
            </ol>
          </div>
        </Form>
      </div>
    );
  }
}

export default DiscoverForm;
